import { useCallback } from 'react';
import {
  AuthForgotPasswordPostRequestBodyBff,
  AuthForgotPasswordPostResponseBodyBff,
  AuthPublicClientsCodeGetResponseBody,
  AuthRecoverPasswordPostRequestBodyBff,
  AuthRecoverPasswordPostResponseBodyBff,
  AuthRecoverPasswordTokenValidationPostRequestBodyBff,
  AuthRecoverPasswordTokenValidationPostResponseBodyBff,
  AuthRefreshPostRequestBodyBff,
  AuthRefreshPostResponseBodyBff,
  AuthSessionGetResponseBody,
  AuthPublicBrandingSubdomainSubdomainGetResponseBody,
} from '@goalsetter/classroom-types/auth-app';
import { AuthPasswordPolicyGetResponseBody } from '@goalsetter/classroom-types/auth';
import { baseUrl, apiBase } from '@/api/client/ApiBase';

export interface PublicPasswordPolicyScope {
  clientCode?: string;
  resetToken?: string;
  claimToken?: string;
  activationToken?: string;
}

const useAuthApi = () => {
  const postForgotPassword = async (
    body: AuthForgotPasswordPostRequestBodyBff,
  ): Promise<AuthForgotPasswordPostResponseBodyBff> => {
    const url = `${baseUrl()}/api/auth/public/forgot-password`;
    return apiBase.post<
      AuthForgotPasswordPostRequestBodyBff,
      AuthForgotPasswordPostResponseBodyBff
    >(url, body);
  };

  const postRecoverPassword = async (
    body: AuthRecoverPasswordPostRequestBodyBff,
  ): Promise<AuthRecoverPasswordPostResponseBodyBff> => {
    const url = `${baseUrl()}/api/auth/public/recover-password`;
    return await apiBase.post<
      AuthRecoverPasswordPostRequestBodyBff,
      AuthRecoverPasswordPostResponseBodyBff
    >(url, body);
  };

  const postRecoverPasswordTokenValidation = async (
    body: AuthRecoverPasswordTokenValidationPostRequestBodyBff,
  ): Promise<AuthRecoverPasswordTokenValidationPostResponseBodyBff> => {
    const url = `${baseUrl()}/api/auth/public/recover-password/token-validation`;
    return await apiBase.post<
      AuthRecoverPasswordTokenValidationPostRequestBodyBff,
      AuthRecoverPasswordTokenValidationPostResponseBodyBff
    >(url, body);
  };

  /**
   * Retrieves client information by code.
   * Supports shared subdomain scenarios where icode (client code) is provided.
   * When icode is provided, it is the client code and is used directly for lookup.
   *
   * @param clientCode - Client unique code, subdomain, or id (typically subdomain from URL)
   * @param icode - Optional client code (same as code field). When provided, searches directly by code=icode
   * @returns Promise resolving to the client data
   */
  const getClientByCode = useCallback(
    async (
      clientCode: string,
      icode?: string | null,
    ): Promise<AuthPublicClientsCodeGetResponseBody> => {
      const url = icode
        ? `${baseUrl()}/api/auth/public/clients/code/${clientCode}?icode=${encodeURIComponent(icode)}`
        : `${baseUrl()}/api/auth/public/clients/code/${clientCode}`;
      return apiBase.get(url);
    },
    [],
  );

  const getClientBrandingBySubdomain = async (
    subdomain: string,
    icode?: string,
  ): Promise<AuthPublicBrandingSubdomainSubdomainGetResponseBody> => {
    const queryParams = icode ? `?icode=${encodeURIComponent(icode)}` : '';
    const url = `${baseUrl()}/api/auth/public/branding/subdomain/${encodeURIComponent(subdomain)}${queryParams}`;
    return apiBase.get(url);
  };

  const postRefreshToken = async (
    body: AuthRefreshPostRequestBodyBff,
  ): Promise<AuthRefreshPostResponseBodyBff> => {
    const url = `${baseUrl()}/api/auth/public/refresh`;
    return apiBase.post<
      AuthRefreshPostRequestBodyBff,
      AuthRefreshPostResponseBodyBff
    >(url, body, { withCredentials: true });
  };

  const validateSession = async (): Promise<AuthSessionGetResponseBody> => {
    const url = `${baseUrl()}/api/auth/public/session`;
    return apiBase.get(url, { withCredentials: true });
  };

  const getPasswordPolicy = async (
    scope: PublicPasswordPolicyScope,
  ): Promise<AuthPasswordPolicyGetResponseBody> => {
    const searchParams = new URLSearchParams();
    (Object.entries(scope) as [string, string | undefined][]).forEach(
      ([key, value]) => {
        if (value) searchParams.set(key, value);
      },
    );
    const suffix = searchParams.size > 0 ? `?${searchParams.toString()}` : '';
    return apiBase.get(`${baseUrl()}/api/auth/public/password-policy${suffix}`);
  };

  return {
    postForgotPassword,
    postRecoverPassword,
    postRecoverPasswordTokenValidation,
    getClientByCode,
    getClientBrandingBySubdomain,
    postRefreshToken,
    validateSession,
    getPasswordPolicy,
  };
};

export default useAuthApi;
